// What is sent to the brain's chat while she is still answering — T446d (25 Oct 2026).
//
// The box stays open while she answers. Whatever is sent meanwhile waits in a queue, drawn at once
// as "You" bubbles, and goes when her answer ends. Typed text folds into the question in flight:
// the first typed item stamps that question's row (`POST /find/:queryId/follow`), the service
// stops her unless she has already done something for it, and the next ask carries both
// messages. A file or a dictated question never folds: it is answered on its own, after. Nor
// does a Reply (T561): it is asked alone, its quote with it; a stopped question keeps its own.
//
// Pure, because the panel's JSX hides every rule here and each deserves a test (follow-ups.test.ts).
/** Restated from `@vera/bff`'s `MAX_ASK_LENGTH`, pinned by the test: a longer ask is a 400. */
export const MAX_ASK_LENGTH = 2_000;
/** Restated from `@vera/bff`'s `REPLY_EXCERPT_MAX`, pinned by the test: a longer highlight is a 400. */
export const REPLY_EXCERPT_MAX = 600;

/**
 * T561: a Reply on a message of the chat. The service is told the row, the side and the words
 * highlighted (`excerpt`, absent for the whole message) and reads the words back itself; `shown`
 * is what the chat draws above the question meanwhile, and is never sent.
 */
export interface ReplyTo {
  queryId: string;
  side: 'ask' | 'answer';
  excerpt?: string;
  shown: string;
}

/** What the find's body says of a Reply: never `shown`, a highlight cut to the service's cap. */
export function replyBody(reply: ReplyTo): {
  queryId: string;
  side: 'ask' | 'answer';
  excerpt?: string;
} {
  const excerpt = reply.excerpt?.trim().slice(0, REPLY_EXCERPT_MAX);
  return { queryId: reply.queryId, side: reply.side, ...(excerpt ? { excerpt } : {}) };
}

export interface Queued {
  /** `file` carries an attachment (words optional); `spoken` was dictated; `text` was typed. */
  kind: 'text' | 'file' | 'spoken';
  words: string;
  file?: File;
  /** A dictated question that carries a file: `kind` is `file`, and the mark rides on. */
  spoken?: true;
  /** T561: the message it replies to. */
  replyTo?: ReplyTo;
}

/** The next ask, shaped as the panel sends it. */
export interface NextAsk {
  words: string;
  file?: File;
  spoken?: true;
  replyTo?: ReplyTo;
}

/** How the ask in flight ended. */
export type Outcome = 'superseded' | 'answered' | 'failed';

/**
 * The leading run of typed items, as many as fit under the service's cap (at least one). A Reply
 * ends the run before it, and when it leads, is the run alone.
 */
function textRun(queue: readonly Queued[], from: string): { count: number; words: string } {
  let words = from;
  let count = 0;
  for (const item of queue) {
    if (item.kind !== 'text') break;
    if (item.replyTo) {
      if (count === 0 && from === '') return { count: 1, words: item.words };
      break;
    }
    const joined = words === '' ? item.words : `${words}\n${item.words}`;
    if (count > 0 && joined.length > MAX_ASK_LENGTH) break;
    words = joined;
    count += 1;
  }
  return { count, words };
}

/**
 * Whether to stamp the question in flight now: its head is typed text, the service said it can
 * be folded, it is not stamped yet, and both messages together still fit in one ask. Over the
 * cap the fold would be refused after her draft was dropped, so the follow-up goes alone, after.
 */
export function shouldFollow(
  queue: readonly Queued[],
  foldable: boolean,
  stamped: boolean,
  inFlightWords: string,
): boolean {
  const head = queue[0];
  if (!foldable || stamped || !head || head.kind !== 'text' || head.replyTo) return false;
  return `${inFlightWords}\n${head.words}`.length <= MAX_ASK_LENGTH;
}

/**
 * What to ask once the question in flight has ended, and what is left in the queue.
 *  - superseded: her stopped question's words plus the leading run of typed items, one ask, still
 *    replying to what the stopped question replied to (`inFlightReply`);
 *  - answered or failed: the leading run of typed items (a burst), or one file, dictated item or
 *    Reply alone. A failed question's queue goes on all the same.
 */
export function nextAsk(
  queue: readonly Queued[],
  outcome: Outcome,
  inFlightWords: string,
  inFlightReply?: ReplyTo,
): { ask: NextAsk | null; rest: readonly Queued[] } {
  const head = queue[0];
  if (outcome === 'superseded') {
    const run = textRun(queue, inFlightWords);
    return {
      ask: { words: run.words, ...(inFlightReply ? { replyTo: inFlightReply } : {}) },
      rest: queue.slice(run.count),
    };
  }
  if (!head) return { ask: null, rest: queue };
  const reply = head.replyTo ? { replyTo: head.replyTo } : {};
  if (head.kind === 'file') {
    return {
      ask: {
        words: head.words,
        ...(head.file ? { file: head.file } : {}),
        ...(head.spoken ? { spoken: true as const } : {}),
        ...reply,
      },
      rest: queue.slice(1),
    };
  }
  if (head.kind === 'spoken')
    return { ask: { words: head.words, spoken: true, ...reply }, rest: queue.slice(1) };
  const run = textRun(queue, '');
  return { ask: { words: run.words, ...reply }, rest: queue.slice(run.count) };
}

/**
 * Whether a new chat's id may go into the URL now. The panel is keyed on the URL's chat, so taking
 * it remounts the panel and drops what is on the composer: a half-typed message, an attached file,
 * an open mic (a read-aloud goes on across it). Nor while anything is in flight or queued.
 */
export function mayTakeUrl(state: {
  asking: boolean;
  queued: number;
  draft: string;
  attached: boolean;
  talk: 'idle' | 'listening' | 'speaking';
}): boolean {
  return (
    !state.asking &&
    state.queued === 0 &&
    state.draft.trim() === '' &&
    !state.attached &&
    state.talk !== 'listening'
  );
}

/**
 * Whether a failed ask's line stays on screen when the ask after it ends. A queued ask answered
 * means the line would sit under that newest answer, and the failed turn is stored and drawn in
 * its own place once the chat is read again.
 */
export function failureStays(chained: boolean, outcome: Outcome): boolean {
  return !(chained && outcome === 'answered');
}

/**
 * T561 review round 1: what the composer holds after a refused Reply, or null to leave it. The
 * service refuses one (`reply_not_found`) before the question is logged, so nothing in the chat
 * keeps it: its words go back on the composer, ahead of anything typed there since.
 */
export function wordsBack(code: string | null, composer: string, asked: string): string | null {
  if (code !== 'reply_not_found') return null;
  return composer.trim() === '' ? asked : `${asked}\n${composer}`;
}

/**
 * T561 fix round 2: the file a refused Reply carried goes back on the composer with its words,
 * unless another has been attached since (that one stays); null to leave the composer's file.
 */
export function fileBack(
  code: string | null,
  attachedNow: File | null,
  file: File | null | undefined,
): File | null {
  if (code !== 'reply_not_found' || !file || attachedNow) return null;
  return file;
}
